/**
 * Embedding Provider API — 임베딩 AI 제공자 관리
 * 지식 파이프라인에서 사용할 임베딩 모델 제공자를 관리합니다.
 */

import apiClient from './client';

export interface EmbeddingProviderStatus {
  connected: boolean;
  apiKeyMasked?: string;
  scope?: string;
  inherited?: boolean;
}

export type EmbeddingAuthType = 'assumeRole' | 'accessKey';

export interface EmbeddingAccount {
  id: string;
  scope: string;
  name: string;
  authType: EmbeddingAuthType;
  region: string;
  externalId?: string;
  roleArn?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  isActive?: boolean;
  inherited?: boolean;
  s3BucketName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEmbeddingAccountInput {
  name: string;
  authType: EmbeddingAuthType;
  region: string;
  roleArn?: string;
  externalId?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  scopeOverride?: string;
  s3BucketName?: string;
}

export interface EmbeddingScopeOption {
  value: string;
  label: string;
  type?: 'tenant' | 'customer' | 'team';
  parentValue?: string;
}

export interface EmbeddingModel {
  modelId: string;
  modelName: string;
  providerName: string;
  dimensions?: number;
  maxTokens?: number;
}

export const embeddingProviderApi = {
  /** 임베딩 제공자 연결 상태 조회 */
  async getProviderStatus(providerId: string): Promise<EmbeddingProviderStatus> {
    const res = await apiClient.get<{ success: boolean; data: EmbeddingProviderStatus }>(
      `/api/admin/embedding-providers/${providerId}/status`
    );
    return res.data.data;
  },

  /** API Key 저장 */
  async saveApiKey(providerId: string, apiKey: string, scopeOverride?: string): Promise<EmbeddingProviderStatus> {
    const res = await apiClient.post<{ success: boolean; data: EmbeddingProviderStatus }>(
      `/api/admin/embedding-providers/${providerId}/apikey`,
      { apiKey, scopeOverride }
    );
    return res.data.data;
  },

  /** 연결 해제 */
  async deleteApiKey(providerId: string): Promise<void> {
    await apiClient.delete(`/api/admin/embedding-providers/${providerId}/apikey`);
  },

  /** 연결 테스트 */
  async testConnection(providerId: string): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/api/admin/embedding-providers/${providerId}/test`
    );
    return res.data.data;
  },

  // ── Bedrock 임베딩 어카운트 ──────────────────────────────────────────────
  /** External ID 선발급 */
  async getExternalId(): Promise<{ externalId: string; serviceAccountId: string; principalArn: string }> {
    const res = await apiClient.get<{ success: boolean; data: { externalId: string; serviceAccountId: string; principalArn: string } }>(
      '/api/admin/embedding-providers/bedrock/external-id'
    );
    return res.data.data;
  },

  /** 어카운트 등록 대상 스코프 선택지 */
  async getScopeOptions(customerId?: string): Promise<EmbeddingScopeOption[]> {
    const params = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    const res = await apiClient.get<{ success: boolean; data: EmbeddingScopeOption[] }>(
      `/api/admin/embedding-providers/bedrock/scope-options${params}`
    );
    return res.data.data;
  },

  /** 임베딩 어카운트 목록 */
  async listAccounts(scope?: string): Promise<EmbeddingAccount[]> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    const res = await apiClient.get<{ success: boolean; data: EmbeddingAccount[] }>(
      `/api/admin/embedding-providers/bedrock/accounts${params}`
    );
    return res.data.data;
  },

  /** 임베딩 어카운트 생성 */
  async createAccount(input: CreateEmbeddingAccountInput): Promise<EmbeddingAccount> {
    const res = await apiClient.post<{ success: boolean; data: EmbeddingAccount }>(
      '/api/admin/embedding-providers/bedrock/accounts',
      input
    );
    return res.data.data;
  },

  /** 임베딩 어카운트 수정 */
  async updateAccount(accountId: string, input: Partial<CreateEmbeddingAccountInput>): Promise<EmbeddingAccount> {
    const res = await apiClient.patch<{ success: boolean; data: EmbeddingAccount }>(
      `/api/admin/embedding-providers/bedrock/accounts/${accountId}`,
      input
    );
    return res.data.data;
  },

  /** 임베딩 어카운트 삭제 */
  async deleteAccount(accountId: string, scope?: string): Promise<void> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    await apiClient.delete(`/api/admin/embedding-providers/bedrock/accounts/${accountId}${params}`);
  },

  /** 임베딩 어카운트 활성화 */
  async activateAccount(accountId: string): Promise<EmbeddingAccount> {
    const res = await apiClient.post<{ success: boolean; data: EmbeddingAccount }>(
      `/api/admin/embedding-providers/bedrock/accounts/${accountId}/activate`
    );
    return res.data.data;
  },

  /** 연결 테스트 (저장 전) */
  async testCredentials(input: Partial<CreateEmbeddingAccountInput>): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      '/api/admin/embedding-providers/bedrock/test',
      input
    );
    return res.data.data;
  },

  /** 연결 테스트 (저장된 계정) */
  async testAccount(accountId: string): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/api/admin/embedding-providers/bedrock/accounts/${accountId}/test`
    );
    return res.data.data;
  },

  /** 사용 가능한 임베딩 모델 목록 조회 */
  async listModels(): Promise<EmbeddingModel[]> {
    const res = await apiClient.get<{ success: boolean; data: EmbeddingModel[] }>(
      '/api/admin/embedding-providers/bedrock/models'
    );
    return res.data.data;
  },

  /** 모든 외부 제공자 연결 상태 일괄 조회 */
  async getAllProviderStatuses(scope?: string): Promise<Record<string, EmbeddingProviderStatus>> {
    const ids = ['openai', 'cohere'];
    const results = await Promise.allSettled(ids.map((id) => embeddingProviderApi.getProviderStatus(id)));
    const map: Record<string, EmbeddingProviderStatus> = {};
    ids.forEach((id, i) => {
      const r = results[i];
      map[id] = r?.status === 'fulfilled' ? r.value : { connected: false };
    });
    return map;
  },
};

export default embeddingProviderApi;
